import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { Form } from '@unform/web';
import { FormHandles, SubmitHandler } from '@unform/core';
import * as Yup from 'yup';

import { Modal, ModalCloseButton } from 'components/shared';
import { FileInput } from 'components/shared/Form';
import { useTranslation, useValidation } from 'hooks';
import { translations } from 'pages/Settings/Clients/ListClients/translations';
import {
  ImportClientsActions,
  ImportClientsState,
} from 'store/ducks/settings/clients';
import { validateExcelColumns } from './validateExcelColumns';

import * as S from './styles';

interface ImportClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ImportClientsModal: React.FC<ImportClientsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { getTranslation } = useTranslation(translations);
  const { handleFormErrors } = useValidation();

  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isFileValid, setIsFileValid] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formKey, setFormKey] = useState(0);

  const { loading } = useSelector<RootStateOrAny>(
    (state: any) => state.importClients
  ) as ImportClientsState;

  // Limpa o arquivo quando o modal é fechado
  useEffect(() => {
    if (!isOpen) {
      // Reseta estados imediatamente
      setSelectedFile(null);
      setIsFileValid(false);
      setValidationError(null);
      setValidating(false);
      
      // Força recriação do form mudando a key
      setFormKey(prev => prev + 1);
    }
  }, [isOpen]);

  // Observa mudanças no campo de arquivo via MutationObserver
  useEffect(() => {
    if (!isOpen) return;

    const validateFile = async (file: File) => {
      setValidating(true);
      setValidationError(null);
      
      try {
        const { isValid, missingColumns } = await validateExcelColumns(file);
        
        if (!isValid) {
          const missingColsText = missingColumns.join(', ');
          setValidationError(
            `${getTranslation('erroColunasInvalidas')}: ${missingColsText}`
          );
          setIsFileValid(false);
        } else {
          setIsFileValid(true);
          setValidationError(null);
        }
      } catch (error) {
        setValidationError('Erro ao validar arquivo');
        setIsFileValid(false);
      } finally {
        setValidating(false);
      }
    };

    // Busca o input de arquivo no DOM
    const timer = setTimeout(() => {
      const input = document.querySelector('input[name="file"]') as HTMLInputElement;
      if (input) {
        fileInputRef.current = input;
        
        const handleChange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          const file = target.files?.[0];
          
          if (file) {
            setSelectedFile(file);
            validateFile(file);
          } else {
            setSelectedFile(null);
            setIsFileValid(false);
            setValidationError(null);
          }
        };

        input.addEventListener('change', handleChange);
        
        return () => {
          input.removeEventListener('change', handleChange);
        };
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isOpen, getTranslation]);

  const handleSubmit = useCallback<SubmitHandler>(
    async (data: any) => {
      try {
        formRef.current?.setErrors({});
        setValidationError(null);

        const acceptExt = [
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];

        const schema = Yup.object().shape({
          file: Yup.mixed()
            .required('Obrigatório')
            .test(
              'fileType',
              'O arquivo deve ser .xlsx ou .xls',
              (value: any) => {
                if (!value) return false;
                return acceptExt.includes(value.type);
              }
            )
            .test(
              'fileSize',
              'O tamanho máximo do arquivo é de 10 MB',
              (value: any) => {
                if (!value) return true;
                return value.size <= 10485760; // 10MB
              }
            ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        if (!isFileValid) {
          setValidationError(getTranslation('erroColunasInvalidas'));
          return;
        }

        const formData = new FormData();
        formData.append('file', data.file);

        dispatch(
          ImportClientsActions.request(
            formData,
            () => {
              if (onSuccess) onSuccess();
              onClose();
            },
            () => {
              // onFailure - erro já é tratado no saga
            }
          )
        );
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, isFileValid, onClose, onSuccess, getTranslation]
  );

  const handleClose = useCallback(() => {
    // Reseta estados
    setValidationError(null);
    setIsFileValid(false);
    setValidating(false);
    setSelectedFile(null);
    
    // Fecha o modal (o useEffect vai limpar quando isOpen mudar)
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClickOutside={handleClose}>
      <S.Container>
        {loading && (
          <S.LoadingOverlay>
            <S.LoadingSpinner />
            <S.LoadingText>Importando clientes...</S.LoadingText>
            <S.LoadingSubText>Por favor, aguarde. Isso pode levar alguns instantes.</S.LoadingSubText>
          </S.LoadingOverlay>
        )}
        
        <ModalCloseButton onClick={handleClose} />
        
        <S.Title>{getTranslation('importarClientes')}</S.Title>
        <S.Description>
          {getTranslation('colunasObrigatorias')}
        </S.Description>

        <Form 
          key={formKey}
          ref={formRef} 
          onSubmit={handleSubmit}
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <FileInput
            name="file"
            label={getTranslation('selecionarArquivo')}
            buttonLabel={getTranslation('selecionarArquivo')}
            accept=".xlsx,.xls"
            required
          />

          {validating && (
            <S.ValidationMessage type="loading">
              {getTranslation('validandoArquivo')}
            </S.ValidationMessage>
          )}

          {validationError && !validating && (
            <S.ValidationMessage type="error">
              {validationError}
            </S.ValidationMessage>
          )}

          {isFileValid && !validating && !loading && (
            <S.ValidationMessage type="info">
              Arquivo válido! Pronto para importar.
            </S.ValidationMessage>
          )}

          <S.ButtonsArea>
            <S.Button type="button" onClick={handleClose} disabled={loading}>
              {getTranslation('cancelar')}
            </S.Button>
            <S.Button
              type="submit"
              variant="primary"
              disabled={loading || validating || !isFileValid}
            >
              {getTranslation('importar')}
            </S.Button>
          </S.ButtonsArea>
        </Form>
      </S.Container>
    </Modal>
  );
};

